// The whole-trace needle lens — hand-off candidates without a focal turn.
//
// The hand-off scan reads the transcript with a model and works backwards
// from what it noticed; this lens goes the other way. Every turn of the
// loaded trace contributes its id-shaped needle runs (the content class
// measured to carry cross-session joins), each needle remembers which SIDE
// of the conversation it appeared on, and one probe pass over the
// conversation index returns every session that carries the same content.
// No turn is privileged and nothing depends on a reader picking a file or
// an anchor first.
//
// Sides make the candidates directional. A needle from this trace's
// assistant turns is something this session PRODUCED; found in another
// session's user/tool rows, the content flowed focal -> counterpart. A
// needle from this trace's user/tool turns is something this session was
// GIVEN; found in another session's assistant rows, the counterpart
// authored it. Same-side matches still join, but claim no direction.

import { partitionProbeResults, extractNeedleRuns } from "./traceElsewhereChannels.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { CLUSTER_HUES } from "./traceAnatomy.js";

// Enough needles to represent a long trace without turning one open into a
// query storm: each needle is its own bounded index search. Sized to the
// lane-diversity guarantee, not just recall: a mailbox trace carries dozens
// of artifact lanes, and a cap the diversity pass exhausts leaves the
// smallest artifacts unpinned — the 0001 mailbox file was the one file on
// the plane without an annotation at cap 24. The consumer runs behind the
// per-browser experimental opt-in and at most one batch per open lanes view,
// so this cap bounds a single opted-in reader's open, not a fleet surface.
export const LENS_NEEDLE_CAP = 40;
// A needle recurring across many of this trace's own turns is its working
// vocabulary (a path it edits, its own session id) — still worth probing,
// but the corpus-side diffuseness cap is what keeps it honest.
//
// Samples are kept PER NEEDLE, two each: a flat per-candidate cap starved
// every pin after the first few — a counterpart matching twenty-five mailbox
// files spent all its samples on the first two, and twenty-three pins had no
// preview to show. Two per needle lets the mirror picker still prefer a
// non-injected hit.
const SAMPLES_PER_NEEDLE = 2;

// A needle carried by hundreds of turns is the trace's working vocabulary;
// the marks only need enough occurrences to place it on the axis.
const NEEDLE_TURNS_CAP = 120;
const NEEDLE_CELLS_CAP = 120;

// -> [{ needle, emit, ingest, firstTurn, turns, cells }], capped. Turn
// numbers are 0-based turn INDICES throughout — the lanes key their cells on
// turnIndex, and a lens speaking a different base put every pin one cell to
// the right of the content it annotated (found live). `cells` is every
// (turn, lane) that carried the needle, when the caller supplies per-segment
// lane hints — a needle inside a file write pins to THAT file's lane, not
// just the turn. Each turn input is { role, text } or { role, segments:
// [{ lane, text, cellTurn? }] }; a segment with no lane contributes to the
// turn's occurrences but pins no cell. `cellTurn` re-homes the PIN alone: a
// tool result's content occurs the turn it is fed back (that is where the
// strip marks it), but its cell is the CALL that fetched it — the only turn
// its lane charts, so a pin at the occurrence turn failed the has-a-cell
// filter and the most join-bearing content drew no pin at all (found live).
//
// Selection is lane-diverse, not purely longest-first: one slot goes to the
// best needle of every distinct artifact lane before remaining slots fill by
// length. Longest-first alone starved exactly the needles the annotations
// exist for — a 26-char mailbox filename lost every slot to 50-char session
// paths, and the most-shared artifact on the plane carried no pin.
export function deriveTraceNeedles(turnInputs = [], { max = LENS_NEEDLE_CAP } = {}) {
  const byNeedle = new Map();
  (turnInputs || []).forEach((t, i) => {
    const side = t?.role === "assistant" ? "emit" : "ingest";
    const segments = Array.isArray(t?.segments) ? t.segments : [{ lane: null, text: t?.text }];
    for (const seg of segments) {
      for (const needle of extractNeedleRuns(seg?.text)) {
        let it = byNeedle.get(needle);
        if (!it) {
          it = { needle, emit: false, ingest: false, firstTurn: i, turns: [], cells: [] };
          byNeedle.set(needle, it);
        }
        it[side] = true;
        if (it.turns.length < NEEDLE_TURNS_CAP && it.turns[it.turns.length - 1] !== i) {
          it.turns.push(i);
        }
        const cellTurn = Number.isInteger(seg?.cellTurn) ? seg.cellTurn : i;
        if (seg?.lane && it.cells.length < NEEDLE_CELLS_CAP
          && !it.cells.some((c) => c.turn === cellTurn && c.lane === seg.lane)) {
          it.cells.push({ turn: cellTurn, lane: seg.lane });
        }
      }
    }
  });
  const ranked = [...byNeedle.values()]
    .sort((a, b) => (b.needle.length - a.needle.length) || (a.firstTurn - b.firstTurn));
  if (ranked.length <= max) return ranked;
  // One slot per artifact lane, and the slot goes to the lane's IDENTITY
  // needle when it has one — a run contained in the lane id itself, i.e. the
  // file's own name. A lane's longest needle is often a prose run unique to
  // this trace (matches nobody), while the filename is what a counterpart
  // that touched the file reliably carries: the 0001 mailbox file matched a
  // real reader and still drew no pin while its slot was spent on an
  // unmatchable content run (found live).
  const byLane = new Map();
  for (const n of ranked) {
    const lane = n.cells[0]?.lane;
    if (!lane) continue;
    if (!byLane.has(lane)) byLane.set(lane, []);
    byLane.get(lane).push(n);
  }
  const picked = new Set();
  // Agents lanes are served FIRST, and their slot goes to CONTENT, never to
  // the lane's identity: an agents lane's id contains the tool-use id, a
  // harness string only monitors echo — while the thread's report content is
  // the one thing the spawned session is guaranteed to share verbatim. With
  // more lanes than slots the generic order starved exactly these, and the
  // session's own thread stayed invisible to the census (found live).
  const laneOrder = [...byLane.keys()].sort((a, b) =>
    (String(b).startsWith("agents:") ? 1 : 0) - (String(a).startsWith("agents:") ? 1 : 0));
  for (const lane of laneOrder) {
    if (picked.size >= max) break;
    const cands = byLane.get(lane);
    if (String(lane).startsWith("agents:")) {
      picked.add(cands[0]);
      continue;
    }
    const laneLower = lane.toLowerCase();
    const identity = cands.find((n) => laneLower.includes(n.needle.toLowerCase()));
    picked.add(identity || cands[0]);
  }
  for (const n of ranked) {
    if (picked.size >= max) break;
    picked.add(n);
  }
  return ranked.filter((n) => picked.has(n));
}

// Locate GIVEN strings on the lanes' own axis -- the landing half of a
// hand-off traversal. deriveTraceNeedles extracts what THIS trace would
// probe with; this walks the same lane-hinted inputs for strings that
// arrived from the OTHER side of a hand-off, so the carrying cells can be
// highlighted the way the census pins its own needles. Case-insensitive
// like the probes that made the strings matches in the first place. Cells
// are reported for EVERY lane, conversation rows included -- which cells are
// drawable is the renderer's law (the same charted-cell gate the census
// pins pass through), and `turns` keeps the conversation-only carriers
// nameable even when nothing is ringed.
// Matching folds case AND whitespace: index-stored strings (word grams
// especially) are lowercased with runs of whitespace collapsed to single
// spaces, while the transcript keeps its original line breaks — the fold
// makes the stored form findable without changing turn/cell attribution.
// -> [{ needle, turns, cells }] for the strings that occur at all.
const foldForLocate = (s) => s.toLowerCase().replace(/\s+/g, " ");

export function locateNeedleStrings(turnInputs = [], strings = []) {
  const wanted = (strings || [])
    .filter((s) => typeof s === "string" && s)
    .map((s) => ({ needle: s, lower: foldForLocate(s), turns: [], cells: [] }));
  if (!wanted.length) return [];
  (turnInputs || []).forEach((t, i) => {
    const segments = Array.isArray(t?.segments) ? t.segments : [{ lane: null, text: t?.text }];
    for (const seg of segments) {
      const text = typeof seg?.text === "string" && seg.text ? foldForLocate(seg.text) : "";
      if (!text) continue;
      for (const w of wanted) {
        if (!text.includes(w.lower)) continue;
        if (w.turns.length < NEEDLE_TURNS_CAP && w.turns[w.turns.length - 1] !== i) w.turns.push(i);
        const cellTurn = Number.isInteger(seg?.cellTurn) ? seg.cellTurn : i;
        if (seg?.lane && w.cells.length < NEEDLE_CELLS_CAP
          && !w.cells.some((c) => c.turn === cellTurn && c.lane === seg.lane)) {
          w.cells.push({ turn: cellTurn, lane: seg.lane });
        }
      }
    }
  });
  return wanted.filter((w) => w.turns.length).map(({ lower, ...w }) => w);
}

// Fold per-needle probe results into one candidate per session.
//
// `needleResults[i]` answers `needles[i]`: { ok, rows } with rows carrying
// session_id / role / part_kind / at / snippet. Directional evidence is
// counted per HIT, gated by the needle's focal side:
//   sent      — a focal-emitted needle in the counterpart's user/tool rows
//   received  — a focal-ingested needle in the counterpart's assistant rows
// Everything else still counts as matched, just without a direction claim.
// Diffuse needles (corpus-side session cap) are excluded from both the
// matches and the denominator, exactly like the file probes.
export function foldTraceNeedleMatches(needles = [], needleResults = [], { focalSessionId = null, focalSpan = null, focalEndpointId = null, sessionInventory = null } = {}) {
  const { counted, diffuse } = partitionProbeResults(needleResults);
  const countedIdx = [];
  (needleResults || []).forEach((r, i) => {
    if (counted.includes(r)) countedIdx.push(i);
  });
  const byId = new Map();
  // The definitional WHO: when the index supplied a session inventory,
  // every counterpart appears with its total shared-string breadth and
  // first carriage, whether or not any surfaced string reached it. The
  // per-needle results below then enrich the sessions they touch with
  // direction, pins, and samples.
  for (const sess of sessionInventory || []) {
    const id = sess?.session_id;
    if (!id || id === focalSessionId) continue;
    byId.set(id, {
      session_id: id, endpoint_id: null,
      matchedSet: new Set(), of: countedIdx.length,
      matchedNeedles: [], needleHits: {}, samples: [],
      sent: 0, received: 0,
      theirPrompts: 0, theirResponses: 0, theirTools: 0,
      sides: sess.sides || null,
      first_seen: sess.first_seen ?? null, last_seen: sess.first_seen ?? null,
      sharedTotal: Number(sess.shared) || 0,
    });
  }
  for (const i of countedIdx) {
    const needle = needles[i];
    for (const r of needleResults[i].rows || []) {
      const id = r?.session_id;
      if (!id) continue;
      let it = byId.get(id);
      if (!it) {
        it = {
          session_id: id, endpoint_id: r.endpoint_id ?? null,
          matchedSet: new Set(), of: countedIdx.length,
          // The matched needle INDICES survive into the row: the axis marks
          // need to know which of the focal trace's needles this candidate
          // shares, to anchor the annotation on the turns that carry them.
          matchedNeedles: [],
          needleHits: {},
          sent: 0, received: 0,
          theirPrompts: 0, theirResponses: 0, theirTools: 0,
          samples: [],
          first_seen: null, last_seen: null,
        };
        byId.set(id, it);
      }
      it.matchedSet.add(i);
      const role = String(r.role || "");
      if (needle?.emit && (role === "user" || role === "tool")) it.sent += 1;
      if (needle?.ingest && role === "assistant") it.received += 1;
      // Where the content lives on THEIR side, by role — "3 in prompt text,
      // 1 in a response" says more than any single hit's timestamp. Counted
      // twice over: for the whole relationship, and PER NEEDLE, so a single
      // file's pin can state that file's own footprint instead of repeating
      // the session total on every pin.
      if (role === "assistant") it.theirResponses += 1;
      else if (role === "tool") it.theirTools += 1;
      else if (role === "user") it.theirPrompts += 1;
      const perNeedle = it.needleHits[i] || (it.needleHits[i] = { prompts: 0, responses: 0, tools: 0 });
      if (role === "assistant") perNeedle.responses += 1;
      else if (role === "tool") perNeedle.tools += 1;
      else if (role === "user") perNeedle.prompts += 1;
      if (!it.matchedNeedles.includes(i)) it.matchedNeedles.push(i);
      const needleStr = needle?.needle ?? "";
      if (it.samples.reduce((n, s) => n + (s.needle === needleStr ? 1 : 0), 0) < SAMPLES_PER_NEEDLE) {
        it.samples.push({
          at: r.at ?? null, role, kind: r.part_kind ?? "",
          needle: needleStr, snippet: r.snippet ?? "",
          indexed: r.indexed === true,
        });
      }
      const t = r.at ?? null;
      if (t && (it.first_seen == null || t < it.first_seen)) it.first_seen = t;
      if (t && (it.last_seen == null || t > it.last_seen)) it.last_seen = t;
    }
  }
  let focal = null;
  if (focalSessionId && byId.has(focalSessionId)) {
    focal = byId.get(focalSessionId);
    byId.delete(focalSessionId);
  }
  // Needles whose cells sit on a sub-agent spawn lane are KIN markers: the
  // task file and spawn ids only circulate between a parent and the thread
  // it spawned (and the watchers that echo both). A non-observer counterpart
  // carrying one is this session's own thread — identity read off structure,
  // not another statistical heuristic.
  const spawnNeedles = new Set();
  (needles || []).forEach((n, i) => {
    if ((n?.cells || []).some((c) => String(c?.lane || "").startsWith("agents:"))) spawnNeedles.add(i);
  });
  // Evidence weight per string: a run counts full, a word gram half (shared
  // phrasing is weaker than a shared id), both discounted by how widely the
  // string is shared. Strings without df (the probe fallback) weigh as rare
  // runs, so the probe path ranks exactly as before.
  const needleWeight = (i) => {
    const n = needles[i] || {};
    const df = Number.isFinite(n.df) && n.df >= 2 ? n.df : 2;
    return (n.gram ? 0.5 : 1) / df;
  };
  const finish = ({ matchedSet, ...rest }) => {
    const row = { ...rest, matched: matchedSet.size };
    row.observer = isObserverShaped(row);
    row.kin = !row.observer && (row.matchedNeedles || []).some((i) => spawnNeedles.has(i));
    row.evidence = (row.matchedNeedles || []).reduce((sum, i) => sum + needleWeight(i), 0);
    // The weak tier: sharing one or two strings — or, without inventory
    // breadth, a single shared phrase or a single string most of a band
    // already carries — is co-occurrence rather than an exchange. Ranked
    // and grouped, never gated — the session stays in every count. A rare
    // run among the surfaced matches vouches a thin overlap out of the
    // tier.
    const gramOnly = (row.matchedNeedles || []).length > 0
      && row.matchedNeedles.every((i) => needles[i]?.gram === true);
    const minDf = Math.min(...(row.matchedNeedles || [])
      .map((i) => (Number.isFinite(needles[i]?.df) ? needles[i].df : 2)));
    const rareRunSurfaced = (row.matchedNeedles || []).some((i) => {
      const n = needles[i] || {};
      // Only a SURFACED string with a real document frequency vouches:
      // coverage-backfilled entries carry no df by design, so a thin
      // session cannot vouch itself out of the tier with its own example
      // string.
      return n.gram !== true && n.coverage !== true && Number.isFinite(n.df) && n.df <= 4;
    });
    row.weak = !row.kin
      && (row.sharedTotal != null
        ? row.sharedTotal <= 2 && !rareRunSurfaced
        : row.matched === 1 && (gramOnly || minDf >= 8));
    // Wall-clock precedence: a counterpart whose earliest hit PREDATES this
    // session's start held the content before this session existed — the
    // strongest direction evidence there is, and pure content + time. It
    // never overrides role-polarized reads; it upgrades the directionless
    // ones and rides every row as context.
    const first = row.first_seen != null ? parseServerTimestamp(row.first_seen) : NaN;
    row.precededFocal = Number.isFinite(first) && Number.isFinite(focalSpan?.from) && first < focalSpan.from;
    // ...and the other end: a counterpart whose EARLIEST hit is already after
    // this session ended picked the content up downstream. Together the two
    // flags order counterparts into a flow around the focal session.
    row.followedFocal = Number.isFinite(first) && Number.isFinite(focalSpan?.to) && first > focalSpan.to;
    // An id-shaped match (no path separator) is stronger evidence than a
    // path match: paths are workspace vocabulary two strangers can share,
    // where a token or hash traveled from one place.
    row.idMatch = (row.matchedNeedles || []).some((i) => {
      const n = needles[i]?.needle || "";
      return n && !n.includes("/");
    });
    // Capture topology: a same-machine join is usually the workspace talking
    // to itself; the cross-machine join is the interesting kind of flow.
    row.sameEndpoint = focalEndpointId != null && row.endpoint_id != null
      ? String(row.endpoint_id) === String(focalEndpointId)
      : null;
    return row;
  };
  // Observer-shaped counterparts sort LAST regardless of match volume: they
  // would otherwise win every entry cap on breadth alone and crowd out the
  // counterparts that exchanged something specific. Ranked, never gated.
  const rows = [...byId.values()]
    .map(finish)
    .sort((a, b) => ((b.kin ? 1 : 0) - (a.kin ? 1 : 0))
      || ((a.observer ? 1 : 0) - (b.observer ? 1 : 0))
      || ((a.weak ? 1 : 0) - (b.weak ? 1 : 0))
      || ((b.sent + b.received) - (a.sent + a.received))
      || (b.evidence - a.evidence)
      || ((b.sharedTotal ?? 0) - (a.sharedTotal ?? 0))
      || ((a.sameEndpoint === true ? 1 : 0) - (b.sameEndpoint === true ? 1 : 0))
      || ((b.idMatch ? 1 : 0) - (a.idMatch ? 1 : 0))
      || (b.matched - a.matched)
      || String(a.session_id).localeCompare(String(b.session_id)));
  return { rows, focal: focal ? finish(focal) : null, of: countedIdx.length, diffuse: diffuse.length };
}

// An observer-shaped counterpart: a session that WATCHES this one — an
// approval or monitor loop fed the transcript and echoing it back — rather
// than one that exchanged work with it. The shape is deterministic and read
// off the row itself: it carries nearly EVERY needle (breadth — an exchange
// carries something specific), in volume, overwhelmingly through ONE channel
// (echo concentrates; exchange spreads across prompts, responses, and tool
// rows). The classification ranks and labels, never gates: the row stays in
// every count, sorted last, wearing "observes" instead of a direction the
// echo would fake.
export function isObserverShaped(row) {
  const of = row?.of || 0;
  const matched = row?.matched || 0;
  if (of < 4 || matched / of < 0.75) return false;
  const p = row?.theirPrompts || 0;
  const r = row?.theirResponses || 0;
  const t = row?.theirTools || 0;
  const total = p + r + t;
  if (total < 12) return false;
  return Math.max(p, r, t) / total >= 0.8;
}

// Harness framing in a counterpart's snippet: injected context (a memory or
// CLAUDE.md carried into their prompt by the harness) rather than something
// they read or said themselves. Naming the channel beats showing the frame.
export function looksInjected(snippet) {
  const s = String(snippet || "");
  return s.includes("<system-reminder>") || s.includes("# claudeMd") || s.includes("<command-name>");
}

// The counterpart-side sample to show for a needle: prefer a hit that is not
// harness framing, so the excerpt reads as THEIR words when any exist.
export function pickMirror(row, needleStr) {
  const hits = (row?.samples || []).filter((s) => s.needle === needleStr);
  if (!hits.length) return null;
  return hits.find((s) => s.snippet && !looksInjected(s.snippet)) || hits[0];
}

// The one-line direction phrase for a candidate row. Plain and literal:
// direction is only claimed when the polarized evidence exists.
export function candidateDirectionLabel(row) {
  if (row.kin) return "sub-agent thread of this session";
  if (row.observer) return "observes this session";
  if (row.sent && row.received) return "round-trip";
  if (row.sent) return "received this session's content";
  if (row.received) return "authored content this session received";
  if (row.precededFocal) return "held this content first";
  return "shares content";
}

// The one-line context badges every counterpart card carries: wall-clock
// precedence and capture topology. Time and machine are the two content-pure
// axes a role-polarized label cannot speak for.
export function rowContextBadges(row) {
  const parts = [];
  if (row?.precededFocal) parts.push("predates this session");
  if (row?.sameEndpoint === true) parts.push("same machine");
  else if (row?.sameEndpoint === false) parts.push("another machine");
  return parts.join(" \u00b7 ");
}

// ── axis annotations ─────────────────────────────────────────────────

// A tooltip lists a few relationships, not every one; the mark's count
// carries the rest.
const MARK_ENTRY_CAP = 4;

// Pin each candidate to the focal turns that carry the needles it matched —
// the joined content's actual location on the axis, not a summary above it.
// One mark per annotated turn: { turn, count, entries: [{ session_id, row,
// needle }] }, entries capped, count exact. Adjacent turns are NOT merged
// here — the strip folds runs at its own zoom, the way the hand-off traffic
// does — but a single needle recurring across a candidate's turns collapses
// to one relationship per turn, so a mailbox polled forty times reads as
// forty annotated turns, not forty relationships on one.
export function buildJoinTurnMarks(needles = [], rows = []) {
  const byTurn = new Map();
  for (const row of rows || []) {
    const seenTurns = new Set();
    for (const i of row?.matchedNeedles || []) {
      const needle = needles[i];
      for (const turn of needle?.turns || []) {
        let mark = byTurn.get(turn);
        if (!mark) {
          mark = { turn, count: 0, sessions: [], entries: [], out: false, in: false };
          byTurn.set(turn, mark);
        }
        // Same direction rule the cell pins use: an emitted needle found
        // elsewhere means this turn's content went OUT; an ingested one
        // found elsewhere means it came IN. Both set is a round-trip.
        if (needle.emit) mark.out = true;
        if (needle.ingest) mark.in = true;
        // One relationship per (candidate, turn): several shared needles on
        // the same turn are one exchange, not several. `sessions` carries
        // every id so a zoom-level fold can count DISTINCT sessions across a
        // merged span instead of summing per-turn counts into an overclaim.
        const key = `${row.session_id}@${turn}`;
        if (seenTurns.has(key)) continue;
        seenTurns.add(key);
        mark.count += 1;
        mark.sessions.push(row.session_id);
        if (mark.entries.length < MARK_ENTRY_CAP) {
          mark.entries.push({
            session_id: row.session_id, row, needle: needle.needle,
            identity: false,
            hits: row.needleHits?.[i] || null,
            mirror: pickMirror(row, needle.needle),
          });
        }
      }
    }
  }
  const marks = [...byTurn.values()].sort((a, b) => a.turn - b.turn);
  // Run dedupe: the same counterparty set joining on consecutive turns is ONE
  // relationship over a stretch, not a fact per turn — a session whose
  // working vocabulary recurs across forty turns read as forty chips all
  // claiming "1" (found live). Merge marks whose DISTINCT session sets are
  // identical across a gap of at most two turns into a run carrying
  // `through`; a different counterparty set always starts a new mark.
  const merged = [];
  for (const m of marks) {
    const prev = merged[merged.length - 1];
    const key = [...new Set(m.sessions)].sort().join("\n");
    if (prev && prev.sessionKey === key && m.turn - prev.through <= 2) {
      prev.through = m.turn;
      prev.out = prev.out || m.out;
      prev.in = prev.in || m.in;
      continue;
    }
    merged.push({ ...m, through: m.turn, sessionKey: key });
  }
  return merged.map(({ sessionKey, ...m }) => m);
}

// The counterpart mix by agent kind, most-common first — the rail tooltip's
// one-line answer to "who is on the other end of all this". Unspecified
// enums read as nothing, so they fold into "unknown".
export function joinKindRollup(rows = []) {
  const byKind = new Map();
  for (const r of rows || []) {
    const kind = r?.agent_type && r.agent_type !== "Unspecified" ? r.agent_type : "unknown";
    byKind.set(kind, (byKind.get(kind) || 0) + 1);
  }
  return [...byKind.entries()]
    .sort((a, b) => (b[1] - a[1]) || String(a[0]).localeCompare(String(b[0])))
    .map(([kind, n]) => `${n} ${kind}`)
    .join(" \u00b7 ");
}

// The dock's hand-off reading for a clicked annotation: every join slot in
// [turn, through], merged — distinct sessions counted across the whole range,
// entries deduped by counterpart. The annotation's tooltip and the dock MUST
// answer with the same number: a span tooltip claiming fifteen sessions that
// opened into a dock claiming six was the same data read at two different
// grains (found live).
export function mergeJoinRange(byTurn, turn, through = turn) {
  if (!byTurn) return null;
  const sessions = new Set();
  const entries = [];
  for (let t = turn; t <= through; t++) {
    const slot = byTurn.get(t);
    if (!slot) continue;
    for (const id of slot.sessions || []) sessions.add(id);
    for (const e of slot.entries || []) {
      if (!entries.some((x) => x.session_id === e.session_id)) entries.push(e);
    }
  }
  if (!sessions.size) return null;
  // Observers sort last here TOO: per-mark entries already rank them down,
  // but an observer matches nearly every needle, so walking a range in turn
  // order hands it the earliest slot back (found live). Cap after sorting so
  // the visible eight are spent on counterparts that exchanged something.
  entries.sort((a, b) => ((a.row?.observer ? 1 : 0) - (b.row?.observer ? 1 : 0)));
  return { turn, through, count: sessions.size, entries: entries.slice(0, 8) };
}

// One stable identity hue per counterpart session, from the same categorical
// palette the topic band draws on, handed out by match strength so the
// sessions that carry the joins are the ones the eye can track. Sessions past
// the palette stay on the neutral join accent — eight distinguishable
// identities is the ceiling of useful, not a limitation to engineer around.
export function assignJoinHues(rows = []) {
  // Observers are excluded outright: on match volume alone one would win a
  // hue slot first, and colour here means a counterpart worth tracking.
  const ranked = [...(rows || [])]
    .filter((r) => r?.session_id && !r.observer)
    .sort((a, b) => (b.matched || 0) - (a.matched || 0));
  const out = new Map();
  for (const r of ranked) {
    if (out.size >= CLUSTER_HUES.length) break;
    if (!out.has(r.session_id)) out.set(r.session_id, CLUSTER_HUES[out.size]);
  }
  return out;
}

// Fold adjacent turn marks into coordination SPANS for the strip's rest
// state. Same zoom-relative fold the strip always used, but the span keeps
// its member marks so a hover can expand back to the full per-turn reading
// — the rest state says "coordination around here", the hover says exactly
// which turns, which direction, with whom. A folded span counts DISTINCT
// sessions across its members (summing per-turn counts would claim more
// counterparties than exist) and ORs their directions.
export function foldJoinSpans(marks = [], cellW = 1, foldPx = 14, maxSpanPx = 160) {
  const out = [];
  for (const m of marks) {
    const prev = out[out.length - 1];
    const through = m.through ?? m.turn;
    // The extent cap keeps chain-adjacency honest at low zoom: with a
    // whole trace at a couple of px per turn, gap-folding alone degenerated
    // into ONE span across the entire axis — a bar that says nothing. A
    // span may absorb neighbours only while it stays a local statement.
    const withinCap = prev && (through - prev.turn) * cellW <= maxSpanPx;
    if (prev && withinCap && (m.turn - prev.through) * cellW < foldPx) {
      prev.through = Math.max(prev.through, through);
      prev.marks.push(m);
      for (const s of m.sessions || []) prev.sessions.add(s);
      prev.out = prev.out || Boolean(m.out);
      prev.in = prev.in || Boolean(m.in);
      for (const e of m.entries || []) {
        if (prev.entries.length >= 4) break;
        if (!prev.entries.some((x) => x.session_id === e.session_id)) prev.entries.push(e);
      }
    } else {
      out.push({
        turn: m.turn, through, marks: [m],
        sessions: new Set(m.sessions || []),
        out: Boolean(m.out), in: Boolean(m.in),
        entries: [...(m.entries || [])],
      });
    }
  }
  return out.map((s) => ({ ...s, count: s.sessions.size }));
}

// The cell-level reading of the same pins: one mark per (lane, turn) that
// carried a matched needle, so the annotation sits ON the write that mailed
// the file or the reply that said the id — the way a document editor pins a
// comment to its block. Only needles with lane-resolved cells contribute;
// everything still appears in the turn marks regardless.
export function buildJoinCellMarks(needles = [], rows = []) {
  const byCell = new Map();
  for (const row of rows || []) {
    const seen = new Set();
    for (const i of row?.matchedNeedles || []) {
      const needle = needles[i];
      for (const cell of needle?.cells || []) {
        const cellKey = `${cell.lane}@${cell.turn}`;
        let mark = byCell.get(cellKey);
        if (!mark) {
          mark = { lane: cell.lane, turn: cell.turn, count: 0, sessions: [], entries: [], out: false, in: false };
          byCell.set(cellKey, mark);
        }
        // The pin's direction reads from the needle's side on THIS trace: an
        // emitted needle found elsewhere means the cell's content went OUT;
        // an ingested one found elsewhere means it came IN.
        if (needle.emit) mark.out = true;
        if (needle.ingest) mark.in = true;
        const key = `${row.session_id}@${cellKey}`;
        const identity = cell.lane.toLowerCase().includes(needle.needle.toLowerCase());
        if (seen.has(key)) {
          // One relationship per (candidate, cell) — but when a later needle
          // is the artifact's own name, the entry upgrades to it: identity is
          // the reading the tooltip should lead with.
          const entry = mark.entries.find((x) => x.session_id === row.session_id);
          if (entry && identity && !entry.identity) {
            entry.needle = needle.needle;
            entry.identity = true;
            entry.hits = row.needleHits?.[i] || null;
            entry.mirror = pickMirror(row, needle.needle);
          }
          continue;
        }
        seen.add(key);
        mark.count += 1;
        mark.sessions.push(row.session_id);
        if (mark.entries.length < MARK_ENTRY_CAP) {
          // The mirror moment: the counterpart's own hit for this needle,
          // when the fold kept a sample for it. `identity` marks a needle
          // that IS the artifact's own name (contained in the lane id) —
          // the tooltip says so in words instead of restating the label.
          mark.entries.push({
            session_id: row.session_id, row, needle: needle.needle,
            identity,
            hits: row.needleHits?.[i] || null,
            mirror: pickMirror(row, needle.needle),
          });
        }
      }
    }
  }
  const cells = [...byCell.values()].sort((a, b) => String(a.lane).localeCompare(String(b.lane)) || (a.turn - b.turn));
  // Same run dedupe the turn marks get, per lane: an edit loop touching one
  // file across a dozen consecutive turns with the same counterparty set is
  // one relationship over a stretch, not a dot per edit (a hundred pins on
  // one busy trace, found live). The pin sits at the run's first turn — the
  // cell the has-a-cell filter checks — and `through` rides along for the
  // tooltip.
  const merged = [];
  for (const m of cells) {
    const prev = merged[merged.length - 1];
    const key = [...new Set(m.sessions)].sort().join("\n");
    if (prev && prev.lane === m.lane && prev.sessionKey === key && m.turn - prev.through <= 2) {
      prev.through = m.turn;
      prev.out = prev.out || m.out;
      prev.in = prev.in || m.in;
      continue;
    }
    merged.push({ ...m, through: m.turn, sessionKey: key });
  }
  return merged
    .map(({ sessionKey, ...m }) => m)
    .sort((a, b) => (a.turn - b.turn) || String(a.lane).localeCompare(String(b.lane)));
}
